import { isParagraphElement } from '@aline/common';
import * as Sentry from '@sentry/react';
import type { Lint, WorkerLinter } from 'harper.js';
import type { Operation, RangeRef } from 'slate';
import { Editor, Point, Range } from 'slate';

import {
  buildSpellcheckLints,
  DISABLED_LINT_RULES,
  getDocumentSegments,
  getLiveSpellcheckRange,
} from './spellcheck-helpers';
import type { IgnoredLintsStore } from './spellcheck-ignored-lints';
import {
  clearSpellcheckStateForTabId,
  EMPTY_SPELLCHECK_RETIRED_FROM,
  getSpellcheckStateForTabId,
  updateSpellcheckStateForTabId,
} from './spellcheck-state-registry';
import type { SpellcheckLint } from './spellcheck-vars';
import { SPELLCHECK_STATUS } from './spellcheck-vars';

const DEBOUNCE_MS = 300;

const PATH_SHIFTING_OPS = new Set([
  'insert_node',
  'merge_node',
  'move_node',
  'remove_node',
  'split_node',
]);

interface TrackedLint {
  /**
   * Harper's own lint, kept so dismissing it can be handed back to the linter. These are
   * wasm-bindgen handles, but the generated bindings register a `FinalizationRegistry`, so dropping
   * the reference on the next pass is enough and there is no `free()` to call.
   */
  harperLint: Lint;
  lint: SpellcheckLint & { rangeRef: RangeRef };
}

/**
 * One tab's spellchecker, kept apart from React. `SpellcheckRunner` holds an instance for the life of
 * the editor and forwards what only React knows: whether spellcheck is on, whether the tab's pane is
 * showing, and that the document or the reader's requests changed. Everything else happens here.
 *
 * Each redline is a Slate range ref, so typing moves it with the text. Publishing those refs once per
 * pass is enough. Paint still hides from the edit point onward in that paragraph, so we do not draw
 * waves under text Harper has not seen; the refs stay so the next pass and clicks are already in the
 * right place.
 *
 * React re-runs effects on every tab switch, so every public method is safe to call again with
 * nothing new to say.
 */
export class SpellcheckSession {
  private editor: Editor;
  private tabId: UUID;
  private documentId: UUID;
  private ignoredLintsStore: IgnoredLintsStore;
  /** Whether this worker has the reader's stored dismissals, and so may replace them. */
  private ignoredLintsRestored = false;
  /** The save in flight, so the next one waits for it rather than racing it. */
  private savingIgnoredLints: Promise<void> = Promise.resolve();
  /** What the reader has dismissed in this document, restored into every worker this tab loads. */

  /** Whether the tab's pane is showing. A hidden pane keeps its worker and rows, but does no work. */
  private connected = false;
  /** `null` until React first says whether spellcheck is on, so that first answer is a change. */
  private enabled: boolean | null = null;

  private linter: WorkerLinter | null = null;
  private loading = false;
  /** Bumped to abandon a load in flight; the worker it produces is released when it lands. */
  private loadToken = 0;
  private runToken = 0;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private restoreApply: (() => void) | null = null;

  /**
   * The text the lints in `tracked` were produced from. Written only where a pass is accepted, so a
   * run that is discarded after its await cannot convince the next one that the redlines it holds
   * still describe the document.
   */
  private lintedText: string | null = null;
  private tracked: TrackedLint[] = [];
  /**
   * Monotonic count of content (non-selection) ops. Comparing it to the last-linted version lets a
   * pass be skipped on caret-only changes without walking the document to rebuild its text.
   */
  private docVersion = 0;
  private lastLintedVersion = -1;

  constructor(options: {
    documentId: UUID;
    editor: Editor;
    ignoredLintsStore: IgnoredLintsStore;
    tabId: UUID;
  }) {
    this.documentId = options.documentId;
    this.editor = options.editor;
    this.ignoredLintsStore = options.ignoredLintsStore;
    this.tabId = options.tabId;
  }

  /**
   * The reader's toggle, availability and document loading, combined. Turning it off releases the
   * worker and everything on screen; turning it on forces a fresh pass over the whole document.
   */
  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;

    if (enabled) {
      this.forceFullRelint();
      updateSpellcheckStateForTabId(this.tabId, state => ({
        ...state,
        checking: true,
        status: this.linter ? SPELLCHECK_STATUS.ready : state.status,
      }));
      if (!this.connected) return;
      this.attachToEditor();
      this.startLoading();
      this.scheduleLint();
      return;
    }

    this.detachFromEditor();
    this.cancelLoading();
    this.cancelScheduledLint();
    this.releaseLinter();
    releaseQueuedRangeRefs(getQueuedIgnoreRequests(this.tabId), this.tracked);
    releaseRangeRefs(this.tracked);
    this.tracked = [];
    /**
     * Everything this tab had on screen goes, and the dismissal queue with it: there is no linter
     * left to hand those to. Written as one update so subscribers see a single change.
     */
    updateSpellcheckStateForTabId(this.tabId, state => ({
      ...state,
      checking: false,
      ignoreRequests: [],
      lints: [],
      ranges: [],
      retiredFromByParagraph: EMPTY_SPELLCHECK_RETIRED_FROM,
      status: SPELLCHECK_STATUS.off,
      typing: false,
    }));
  }

  /**
   * The tab's pane is showing. Returns the disconnect for when it is hidden, which keeps the worker
   * and the rows: a hidden pane is not unmounted, and switching back should not pay for a new worker
   * or a pass over the whole document.
   */
  connect(): () => void {
    this.connected = true;
    if (this.enabled) {
      this.attachToEditor();
      this.startLoading();
      // Hiding cleared what was on screen; the rows are still here, so show them again.
      publishLints(this.tabId, this.tracked);
      /**
       * Only the editor hook counts versions, and hiding removes it, so a collaborator's edits while
       * the pane was hidden leave no trace. Check again rather than trust the count: an untouched
       * document takes the same-text shortcut and never reaches Harper.
       */
      this.lastLintedVersion = -1;
      this.scheduleLint();
      this.flushIgnoreRequests();
    }

    return () => {
      this.connected = false;
      this.detachFromEditor();
      this.cancelLoading();
      this.cancelScheduledLint();
      clearSpellcheckStateForTabId(this.tabId);
    };
  }

  /**
   * The document changed, or the worker became ready, or a dismissal needs a fresh pass. Restarts the
   * debounce; a pass runs once the reader pauses, unless nothing has changed since the last one.
   */
  scheduleLint(): void {
    this.cancelScheduledLint();
    const { linter } = this;
    if (!this.connected || !this.enabled || !linter) return;
    if (this.docVersion === this.lastLintedVersion) return;

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.runPass(linter);
    }, DEBOUNCE_MS);
  }

  /**
   * Hand the reader's queued dismissals to Harper. Harper owns the ignore list and matches on its
   * own context hash. Ignoring doesn't edit the document, so nothing would schedule the pass that
   * drops the other copies; hence the re-lint.
   *
   * Kept queued rather than dropped while it cannot run: `lintedText` is empty between a dismissal
   * being accepted and the forced pass finishing, so a second dismissal raised in that window has
   * nothing to be matched against yet. A pass calls this again once it publishes.
   */
  flushIgnoreRequests(): void {
    const requests = getQueuedIgnoreRequests(this.tabId);
    const { lintedText: source, linter } = this;
    if (!this.connected || requests.length === 0 || !linter || source === null) return;

    const dismissed = this.tracked.filter(row =>
      requests.some(request => isSameProblem(request, row.lint))
    );
    releaseQueuedRangeRefs(requests, this.tracked);
    updateSpellcheckStateForTabId(this.tabId, state => ({ ...state, ignoreRequests: [] }));
    if (dismissed.length === 0) return;

    const trackedBefore = this.tracked;
    this.tracked = trackedBefore.filter(row => !dismissed.includes(row));
    const trackedAfterRemoval = this.tracked;
    publishLints(this.tabId, this.tracked);

    /**
     * Retired before Harper is told, not after: a pass asked earlier still has the problem in its
     * answer, so landing now would flash the card back and send the re-lint down the same-text
     * shortcut.
     */
    this.runToken++;

    void this.dismissWithHarper({ dismissed, linter, source, trackedAfterRemoval, trackedBefore });
  }

  /**
   * Tells Harper to stop reporting these problems and keeps a record of them. Harper refusing puts
   * the cards back, since nothing was dismissed after all.
   */
  private async dismissWithHarper(options: {
    dismissed: TrackedLint[];
    linter: WorkerLinter;
    source: string;
    trackedAfterRemoval: TrackedLint[];
    trackedBefore: TrackedLint[];
  }): Promise<void> {
    const { dismissed, linter, source, trackedAfterRemoval, trackedBefore } = options;

    try {
      await linter.ignoreLints(
        source,
        dismissed.map(row => row.harperLint)
      );
      // The dropped rows are no longer on a pass, so stop transforming their refs.
      releaseRangeRefs(dismissed);
      this.forceFullRelint();
      this.scheduleLint();
      // Last, because it may reach the server: the pass above only needs its timer set.
      await this.recordIgnoredLints(linter);
    } catch (error: unknown) {
      // Turning spellcheck off is the reader's choice, not a failure worth reporting.
      if (this.enabled) Sentry.captureException(error, { tags: { feature: 'spellcheck' } });
      /**
       * The pass retired above will not report, and it had already marked its version as linted,
       * so nothing would run again: the spinner would hang and paint would stay hidden from the
       * edit point. Stop the spinner and ask for a fresh pass to replace what it dropped.
       */
      finishPass(this.tabId);
      this.forceFullRelint();
      this.scheduleLint();
      /**
       * Harper kept the lints, so put the rows back. Only when nothing else has replaced the
       * list, and as the whole array: appending would show a problem twice and out of order.
       * Rows that are not coming back release their refs; the restored ones still need theirs.
       */
      if (this.tracked !== trackedAfterRemoval) {
        releaseRangeRefs(dismissed);
        return;
      }
      this.tracked = trackedBefore;
      publishLints(this.tabId, this.tracked);
    }
  }

  /**
   * An accepted suggestion leaves the list at once rather than at the next pass. The edit it makes
   * schedules that pass, which rebuilds every row from Harper and settles what is really left.
   */
  handleAccepted(): void {
    const accepted = getSpellcheckStateForTabId(this.tabId)?.accepted ?? [];
    if (accepted.length === 0) return;
    updateSpellcheckStateForTabId(this.tabId, state => ({ ...state, accepted: [] }));

    const remaining: TrackedLint[] = [];
    const removed: TrackedLint[] = [];
    for (const row of this.tracked) {
      if (accepted.some(lint => isSameProblem(lint, row.lint))) removed.push(row);
      else remaining.push(row);
    }
    if (removed.length === 0) return;

    releaseRangeRefs(removed);
    this.tracked = remaining;
    publishLints(this.tabId, remaining);
  }

  /**
   * The editor is gone for good: closing the tab, leaving the editor, or the error boundary
   * replacing the editor tree. Releases the worker, with its 15.6 MB wasm heap, and every range ref.
   */
  dispose(): void {
    this.cancelLoading();
    this.cancelScheduledLint();
    /**
     * A pass already waiting on Harper cannot be cancelled, so retire its token: when it lands it
     * drops its lints rather than publishing them and taking range refs on an editor that is gone.
     */
    this.runToken++;
    this.releaseLinter();
    releaseQueuedRangeRefs(getQueuedIgnoreRequests(this.tabId), this.tracked);
    releaseRangeRefs(this.tracked);
    this.tracked = [];
  }

  /**
   * Hands the reader's dismissals back to a fresh worker. Held until this finishes, so the first
   * pass cannot report a problem the reader has already dealt with.
   */
  private async restoreIgnoredLints(linter: WorkerLinter, token: number): Promise<void> {
    let restored: string | null;
    try {
      restored = await this.ignoredLintsStore.load(this.documentId);
    } catch (error: unknown) {
      /**
       * Spellchecking a document is worth more than remembering what was dismissed in it. Carry on
       * with nothing restored: a dismissed problem may come back, which beats no spellchecker.
       */
      Sentry.captureException(error, { tags: { feature: 'spellcheck' } });
      return;
    }
    // A load this slow belongs to a worker nobody is waiting for, so it keeps its answer to itself.
    if (token !== this.loadToken) return;

    try {
      // Straight back as it was stored: Harper wrote this text and Harper is the only reader.
      if (restored) await linter.importIgnoredLints(restored);
      this.ignoredLintsRestored = true;
    } catch (error: unknown) {
      Sentry.captureException(error, { tags: { feature: 'spellcheck' } });
    }
  }

  /**
   * Hands Harper's whole ignore list to the store, replacing what was there. Harper is the one
   * that knows what it is ignoring, so asking it beats tracking the hashes ourselves.
   */
  private async recordIgnoredLints(linter: WorkerLinter): Promise<void> {
    if (!this.ignoredLintsRestored) return;

    /**
     * Queued behind the save before it, never sent alongside. A save replaces the whole list, so
     * two in flight at once can land in the wrong order and leave the shorter one stored, losing
     * every dismissal raised after it. Exporting inside the queue also means each save carries
     * what Harper holds when its turn comes, not when it was asked for.
     */
    this.savingIgnoredLints = this.savingIgnoredLints.then(async () => {
      if (this.linter !== linter) return;

      try {
        await this.ignoredLintsStore.save(this.documentId, await linter.exportIgnoredLints());
      } catch (error: unknown) {
        /**
         * Harper has taken the dismissal, so the card is gone for this session either way. Letting
         * this reach the caller would read as Harper refusing it and put the card back on screen.
         * The cost of a failed write is the dismissal not surviving a reload.
         */
        Sentry.captureException(error, { tags: { feature: 'spellcheck' } });
      }
    });

    return this.savingIgnoredLints;
  }

  private startLoading(): void {
    if (this.linter || this.loading) return;
    this.loading = true;
    const token = ++this.loadToken;
    setSpellcheckStatus(this.tabId, SPELLCHECK_STATUS.loading);
    void this.load(token);
  }

  private async load(token: number): Promise<void> {
    try {
      const linter = await loadLinter();
      if (token !== this.loadToken) {
        await disposeLinter(linter);
        return;
      }

      await this.restoreIgnoredLints(linter, token);
      if (token !== this.loadToken) {
        await disposeLinter(linter);
        return;
      }

      this.loading = false;
      this.linter = linter;
      setSpellcheckStatus(this.tabId, SPELLCHECK_STATUS.ready);
      this.scheduleLint();
      this.flushIgnoreRequests();
    } catch (error: unknown) {
      if (token !== this.loadToken) return;
      this.loading = false;
      /**
       * No pass will ever run to clear these, and the drawer shows a spinner while `checking`, so
       * leaving it set would hide the failure behind "Checking…" for good.
       */
      updateSpellcheckStateForTabId(this.tabId, state => ({
        ...state,
        checking: false,
        status: `Failed: ${error instanceof Error ? error.message : String(error)}`,
        typing: false,
      }));
      Sentry.captureException(error, { tags: { feature: 'spellcheck' } });
    }
  }

  /** Makes the next pass reach Harper even though the document has not changed since the last one. */
  private forceFullRelint(): void {
    this.lintedText = null;
    this.lastLintedVersion = -1;
  }

  /** Abandons a load in flight, so turning back on or showing the pane again starts a fresh one. */
  private cancelLoading(): void {
    if (!this.loading) return;
    this.loading = false;
    this.loadToken++;
  }

  private releaseLinter(): void {
    const { linter } = this;
    this.linter = null;
    this.ignoredLintsRestored = false;
    if (linter) void disposeLinter(linter);
  }

  private cancelScheduledLint(): void {
    if (this.debounceTimer === null) return;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
  }

  private async runPass(linter: WorkerLinter): Promise<void> {
    const version = this.docVersion;
    const { fieldRanges, segments, text } = getDocumentSegments(this.editor);
    this.lastLintedVersion = version;
    if (text === this.lintedText) {
      finishPass(this.tabId, { clearRetirement: true });
      return;
    }
    const token = ++this.runToken;
    updateSpellcheckStateForTabId(this.tabId, state => ({ ...state, checking: true }));
    let lints: Lint[];
    try {
      lints = await linter.lint(text);
    } catch (error: unknown) {
      /**
       * Only this run can stop its own spinner. If a newer run has taken over, or spellcheck was
       * turned off, the spinner already belongs to them, and the error is not news.
       */
      if (token === this.runToken && this.enabled) {
        /**
         * Nothing was checked, so the version marker set before the call has to go back. Left in
         * place it reads as "this document is done", and every later pass is skipped until the
         * reader happens to type.
         */
        this.lastLintedVersion = -1;
        Sentry.captureException(error, { tags: { feature: 'spellcheck' } });
        finishPass(this.tabId);
      }
      return;
    }

    // A newer run has taken over (it owns the spinner now), or the checker was turned off.
    if (token !== this.runToken) return;
    // The pane was hidden while this ran, so its positions may be stale; `connect` checks again.
    if (!this.connected) return;
    if (this.docVersion !== version) return;
    if (!this.enabled) {
      finishPass(this.tabId);
      return;
    }

    const tracked: TrackedLint[] = [];
    for (const { harperLint, lint } of buildSpellcheckLints(lints, segments, fieldRanges)) {
      /**
       * Inward: a character typed on either edge of the word sits outside the lint. Insertions
       * inside it stay covered until the next pass.
       */
      const rangeRef = Editor.rangeRef(this.editor, lint.range, { affinity: 'inward' });
      tracked.push({ harperLint, lint: { ...lint, rangeRef } });
    }
    // A queued dismissal is matched by where its word is now, so its ref has to keep moving.
    const queued = getQueuedIgnoreRequests(this.tabId);
    releaseRangeRefs(this.tracked.filter(row => !queued.includes(row.lint)));
    this.tracked = tracked;
    this.lintedText = text;
    publishLints(this.tabId, tracked, { clearRetirement: true });
    finishPass(this.tabId);
    // The linted text is known again, which is what a queued dismissal was waiting for.
    this.flushIgnoreRequests();
  }

  /**
   * Count content changes for the debounce, and hide paint from the edit point onward. Range refs
   * still update themselves; we do not republish them.
   */
  private attachToEditor(): void {
    if (this.restoreApply) return;
    const { editor, tabId } = this;
    const { apply } = editor;
    editor.apply = op => {
      const edit = getOpEditPoint(editor, op);
      const isPathShifting = PATH_SHIFTING_OPS.has(op.type);
      apply(op);
      if (op.type === 'set_selection') return;
      this.docVersion++;
      updateSpellcheckStateForTabId(tabId, state => {
        let { retiredFromByParagraph, typing } = state;
        let changed = false;
        if (!typing) {
          typing = true;
          changed = true;
        }
        if (isPathShifting) {
          const next = retireAllParagraphs(editor, this.tracked, retiredFromByParagraph);
          if (next !== retiredFromByParagraph) {
            retiredFromByParagraph = next;
            changed = true;
          }
        } else if (
          edit &&
          widensRetirement(retiredFromByParagraph.get(edit.paragraphKey), edit.point)
        ) {
          const next = new Map(retiredFromByParagraph);
          next.set(edit.paragraphKey, edit.point);
          retiredFromByParagraph = next;
          changed = true;
        }
        if (!changed) return state;
        return { ...state, retiredFromByParagraph, typing };
      });
    };
    this.restoreApply = () => {
      editor.apply = apply;
    };
  }

  private detachFromEditor(): void {
    this.restoreApply?.();
    this.restoreApply = null;
  }
}

/** Releasing the worker is best effort: nothing is left to tell if it refuses. */
async function disposeLinter(linter: WorkerLinter): Promise<void> {
  try {
    await linter.dispose();
  } catch {
    // The tab is going or the worker is already gone; either way there is nothing to do.
  }
}

/** Harper and its 15.6 MB wasm load on demand, never with the main bundle. */
async function loadLinter(): Promise<WorkerLinter> {
  const [{ WorkerLinter }, { binary }] = await Promise.all([
    import('harper.js'),
    import('harper.js/binary'),
  ]);
  const linter = new WorkerLinter({ binary });
  await linter.setup();
  await linter.setLintConfig({
    ...(await linter.getDefaultLintConfig()),
    ...DISABLED_LINT_RULES,
  });
  return linter;
}

/** A pass has stopped running, whether it finished, was taken over, or was switched off. */
function finishPass(tabId: UUID, options?: { clearRetirement?: boolean }): void {
  updateSpellcheckStateForTabId(tabId, state => {
    const retiredFromByParagraph = options?.clearRetirement
      ? EMPTY_SPELLCHECK_RETIRED_FROM
      : state.retiredFromByParagraph;
    if (!state.checking && !state.typing && retiredFromByParagraph === state.retiredFromByParagraph)
      return state;
    return { ...state, checking: false, retiredFromByParagraph, typing: false };
  });
}

function setSpellcheckStatus(tabId: UUID, status: string): void {
  updateSpellcheckStateForTabId(tabId, state => ({ ...state, status }));
}

/**
 * A dismissal is queued against the lint object of the pass it was raised in, but the pass that
 * finally hands it over has rebuilt every row, so comparing by identity would never match. The
 * flagged text and where it sits now name the same problem across passes. Where Harper found it
 * is not enough: typing before the word while the dismissal waits moves it.
 */
function isSameProblem(left: SpellcheckLint, right: SpellcheckLint): boolean {
  if (left === right) return true;
  if (left.problem !== right.problem) return false;
  const leftRange = getLiveSpellcheckRange(left);
  const rightRange = getLiveSpellcheckRange(right);
  return Boolean(leftRange && rightRange && Range.equals(leftRange, rightRange));
}

function publishLints(
  tabId: UUID,
  tracked: TrackedLint[],
  options?: { clearRetirement?: boolean }
): void {
  updateSpellcheckStateForTabId(tabId, state => {
    /**
     * A dismissal the reader has pressed but Harper has not been told about yet: its card went with
     * the press, and republishing the rows would put it back until the queue is handed over.
     */
    const visible = tracked.filter(
      row => !state.ignoreRequests.some(request => isSameProblem(request, row.lint))
    );
    return {
      ...state,
      lints: visible.map(row => row.lint),
      ranges: visible.map(row => row.lint.rangeRef),
      retiredFromByParagraph: options?.clearRetirement
        ? EMPTY_SPELLCHECK_RETIRED_FROM
        : state.retiredFromByParagraph,
    };
  });
}

function releaseRangeRefs(tracked: readonly TrackedLint[]): void {
  for (const row of tracked) row.lint.rangeRef.unref();
}

function getQueuedIgnoreRequests(tabId: UUID): readonly SpellcheckLint[] {
  return getSpellcheckStateForTabId(tabId)?.ignoreRequests ?? [];
}

/**
 * A dismissal raised against an older pass holds that pass's range ref, which the pass replacing it
 * left alive for the match. Nothing else owns it, so it is released once the queue is done with it.
 */
function releaseQueuedRangeRefs(
  requests: readonly SpellcheckLint[],
  tracked: readonly TrackedLint[]
): void {
  for (const request of requests) {
    if (!tracked.some(row => row.lint === request)) request.rangeRef?.unref();
  }
}

function widensRetirement(previous: Point | null | undefined, point: Point | null): boolean {
  if (previous === undefined) return true;
  if (previous === null) return false;
  return point === null || Point.compare(point, previous) < 0;
}

function retireAllParagraphs(
  editor: Editor,
  tracked: readonly TrackedLint[],
  retiredFrom: ReadonlyMap<string, Point | null>
): ReadonlyMap<string, Point | null> {
  let changed = false;
  const next = new Map(retiredFrom);
  for (const row of tracked) {
    const range = row.lint.rangeRef.current;
    if (range && Editor.hasPath(editor, range.anchor.path)) {
      const entry = Editor.above(editor, { at: range.anchor, match: isParagraphElement });
      if (entry) {
        const key = entry[1].join(',');
        if (next.get(key) !== null) {
          next.set(key, null);
          changed = true;
        }
      }
    }
  }
  return changed ? next : retiredFrom;
}

/**
 * Where an operation starts hiding paint: the paragraph it edits, and the point from which
 * redlines should go. Read before the operation is applied, while its path still refers to the
 * document it was built against.
 */
function getOpEditPoint(
  editor: Editor,
  op: Operation
): { paragraphKey: string; point: Point | null } | null {
  if (op.type === 'set_selection') return null;
  if (!Editor.hasPath(editor, op.path)) return null;
  const entry = Editor.above(editor, { at: op.path, match: isParagraphElement });
  if (!entry) return null;
  const point =
    op.type === 'insert_text' || op.type === 'remove_text'
      ? { offset: op.offset, path: op.path }
      : null;
  return { paragraphKey: entry[1].join(','), point };
}
